import React from 'react';
import { Box, CssBaseline, useTheme, useMediaQuery } from '@mui/material';
import Header from './Header';
import Sidebar from './Sidebar';
// import logo from '../../assets/foster-logo.png'; // Currently unused
import { useAuth } from '../../hooks/useAuth';
import { useOnboarding } from '../../contexts/OnboardingContext';
import OnboardingModal from '../OnboardingModal';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, isAuthenticated } = useAuth();
  const { showOnboarding, setShowOnboarding, defaultTab, redirectOnSave } = useOnboarding();

  // Close sidebar on mobile when route changes
  React.useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Header onMenuClick={toggleSidebar} isAuthenticated={isAuthenticated} user={user} />
      {isAuthenticated && <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1.5,
          mt: isMobile ? 8 : 8, // Removed extra space for mobile search bar
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)', // Account for header height
          display: 'flex',
          flexDirection: 'column',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(isMobile ? {
            marginLeft: 0,
            width: '100%',
          } : {
            marginLeft: sidebarOpen && isAuthenticated ? '10px' : 0,
            width: sidebarOpen && isAuthenticated ? 'calc(100% - 240px)' : '100%',
          }),
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            transition: theme.transitions.create(['opacity', 'transform'], {
              duration: theme.transitions.duration.enteringScreen,
              easing: theme.transitions.easing.easeOut,
            }),
            opacity: 1,
            transform: 'translateY(0)',
            '&.entering': {
              opacity: 0,
              transform: 'translateY(20px)',
            },
          }}
        >
          {children}
        </Box>
      </Box>
      
      {/* Onboarding Modal */}
      <OnboardingModal 
        open={showOnboarding} 
        onClose={() => setShowOnboarding(false)} 
        defaultTab={defaultTab}
        redirectOnSave={redirectOnSave}
      />
    </Box>
  );
};

export default Layout;
